import { useEffect, useRef, useState, type ReactNode } from 'react'
import { ArrowRight, CalendarClock, ShieldCheck, X } from 'lucide-react'
import { useGetMonitoringStatusQuery } from '../services/sessionMonitoringApi'
import { useGetMonitoringQuery, useSetMonitoringMutation, useGetTestingSchedulesQuery } from '../services/projectDataApi'
import ModalPortal from './ModalPortal'
import SelfHealingSettings from './SelfHealingSettings'
import TestingSchedules from './TestingSchedules'
import './StartQaForm.css'
import './TestingSetup.css'

function SetupDialog({ title, subtitle, onClose, children }: { title: string; subtitle: string; onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null)
  const closeRef = useRef(onClose)
  closeRef.current = onClose
  useEffect(() => {
    const previousFocus = document.activeElement
    const panel = ref.current
    panel?.querySelector<HTMLButtonElement>('button')?.focus()
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); closeRef.current(); return }
      if (event.key !== 'Tab' || !panel) return
      const controls = Array.from(panel.querySelectorAll<HTMLElement>('button, a[href], input, select, textarea, summary, [tabindex="0"]'))
        .filter(element => !element.matches(':disabled') && element.getClientRects().length > 0)
      const first = controls[0]
      const last = controls[controls.length - 1]
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus() }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus() }
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      if (previousFocus instanceof HTMLElement) previousFocus.focus()
    }
  }, [])
  return <ModalPortal><div className="np-overlay" onMouseDown={event => { if (event.target === event.currentTarget) onClose() }}>
    <div ref={ref} className="np-modal" role="dialog" aria-modal="true" aria-labelledby="testing-setup-title" aria-describedby="testing-setup-subtitle">
      <header className="np-modal__head"><div className="np-modal__heading"><div>
        <h2 id="testing-setup-title" className="np-modal__title">{title}</h2>
        <p id="testing-setup-subtitle" className="np-modal__lead">{subtitle}</p>
      </div></div><button type="button" className="np-modal__close" aria-label="Close setup" onClick={onClose}><X size={18} aria-hidden /></button></header>
      <div className="np-modal__body testing-setup-content">{children}</div>
    </div>
  </div></ModalPortal>
}

export default function TestingSetupActions({ projectId }: { projectId: string }) {
  const monitoring = useGetMonitoringStatusQuery(projectId, { pollingInterval: 30000, skipPollingIfUnfocused: true })
  const schedules = useGetTestingSchedulesQuery(projectId, { pollingInterval: 60000, skipPollingIfUnfocused: true })
  const capture = useGetMonitoringQuery(projectId)
  const [setMonitoring, dismissal] = useSetMonitoringMutation()
  const [open, setOpen] = useState<'healing' | 'testing' | null>(null)
  const status = monitoring.currentData
  const canEdit = status?.can_edit === true
  const needsHealing = status && capture.currentData && capture.currentData.choice !== 'no' && (!status.configured || !status.enabled)
  const needsTesting = schedules.currentData && !schedules.currentData.schedules.some(schedule => schedule.config.enabled)
  return <>
    {canEdit && (needsHealing || needsTesting) && <div className="testing-setup-actions" aria-label="Set up automatic testing">
      {needsHealing && <button className="testing-setup-card" type="button" onClick={() => setOpen('healing')}>
        <span className="testing-setup-card__icon"><ShieldCheck size={22} aria-hidden /></span><span className="testing-setup-card__text"><strong>Setup Self Healing</strong><span>Watch user sessions for bugs</span></span><ArrowRight className="testing-setup-card__arrow" size={18} aria-hidden />
      </button>}
      {needsTesting && <button className="testing-setup-card" type="button" onClick={() => setOpen('testing')}>
        <span className="testing-setup-card__icon"><CalendarClock size={22} aria-hidden /></span><span className="testing-setup-card__text"><strong>Setup Continuous Testing</strong><span>Test for bugs on a schedule</span></span><ArrowRight className="testing-setup-card__arrow" size={18} aria-hidden />
      </button>}
    </div>}
    {schedules.isError && <p role="alert">Could not check testing schedules. <button type="button" onClick={() => void schedules.refetch()}>Retry</button></p>}
    {open === 'healing' && <SetupDialog title="Setup Self Healing" subtitle="Watch user sessions for bugs" onClose={() => setOpen(null)}>
      <SelfHealingSettings projectId={projectId} setup />
      <footer className="testing-setup__actions">
        <button type="button" onClick={() => setOpen(null)}>Done</button>
        <button type="button" disabled={dismissal.isLoading} onClick={() => {
          void setMonitoring({ action: 'preference', project_id: projectId, choice: 'no' }).unwrap()
            .then(() => setOpen(null)).catch(() => {})
        }}>Dismiss setup reminder permanently</button>
      </footer>
      {dismissal.isError && <p role="alert">Could not dismiss the reminder. Please try again.</p>}
    </SetupDialog>}
    {open === 'testing' && <SetupDialog title="Setup Continuous Testing" subtitle="Test for bugs on a schedule" onClose={() => setOpen(null)}>
      <p>Choose when QA should test your app and how much each run can spend. You can change or pause schedules in Project Settings.</p>
      <TestingSchedules projectId={projectId} canEdit={canEdit} setup onDone={() => setOpen(null)} />
    </SetupDialog>}
  </>
}
